import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, FolderOpen, Link2 } from "lucide-react";
import { DecisionCase } from "@/components/case/DecisionCase";
import { HelmShareComparison } from "@/components/case/HelmShareComparison";

const AttachToHousehold = ({ caseId, onAttached }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const search = async () => {
    try { const d = (await api.get("/agent/households", { params: { q } })).data; setRows((Array.isArray(d) ? d : d.households || []).slice(0, 8)); }
    catch { setRows([]); }
  };
  const attach = async (hid) => {
    try { await api.post(`/agent/cases/${caseId}/attach`, { household_id: hid }); toast.success("Case saved to the household."); onAttached(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save to that household."); }
  };
  return (
    <div className="rounded-xl border border-[#D9D6CC] bg-white p-4" data-testid="case-attach">
      <p className="text-sm font-semibold text-[#0B192C]">Unsaved case — save it to an existing household when you're ready</p>
      <div className="mt-2 flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your households by name" data-testid="case-attach-search" />
        <Button variant="outline" onClick={search} data-testid="case-attach-search-btn">Search</Button>
      </div>
      <ul className="mt-2 space-y-1">
        {rows.map((h) => (
          <li key={h.household_id || h.id}>
            <button type="button" onClick={() => attach(h.household_id || h.id)} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-[#F4F1E8]" data-testid={`case-attach-${h.household_id || h.id}`}>
              <Link2 className="h-4 w-4 text-[#1E3E62]" />{h.name || h.household_name || "Household"}<span className="text-xs text-[#5A6B7B]">{[h.state, h.is_test ? "TEST" : null, (h.household_id || "").slice(-6)].filter(Boolean).join(" · ")}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const HelmPlanFinder = ({ householdId, seed }) => {
  const [cases, setCases] = useState([]);
  const [open, setOpen] = useState(null);
  const [seedFor, setSeedFor] = useState(null);
  const [cur, setCur] = useState(null);
  const { search } = useLocation();
  useEffect(() => { const c = new URLSearchParams(search).get("case"); if (c) setOpen(c); }, [search]);
  const load = useCallback(() => api.get("/agent/cases", { params: householdId ? { household_id: householdId } : {} })
    .then((r) => setCases(r.data.cases || [])).catch(() => {}), [householdId]);
  useEffect(() => { load(); }, [load]);
  const create = useCallback(async (withSeed) => {
    try { const r = await api.post("/agent/cases", { household_id: householdId || null }); setOpen(r.data.case_id); if (withSeed) setSeedFor({ id: r.data.case_id, text: withSeed }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't start a case."); }
  }, [householdId, load]);
  useEffect(() => { if (seed) create(seed); }, [seed, create]);
  return (
    <div className="space-y-4" data-testid="helm-plan-finder">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => create()} className="rounded-full bg-[#0B192C] text-white" data-testid="case-new-button"><Plus className="mr-1 h-4 w-4" />New plan-finder case</Button>
        {cases.map((c) => (
          <button key={c.case_id} type="button" onClick={() => setOpen(c.case_id)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm ${open === c.case_id ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-[#BCCCDC] text-[#1E3E62]"}`}
            data-testid={`case-open-${c.case_id}`}>
            <FolderOpen className="h-3.5 w-3.5" />{c.facts?.member_name || (c.facts?.zip ? `ZIP ${c.facts.zip}` : "New case")} · {(c.updated_at || "").slice(0, 10)}
          </button>
        ))}
      </div>
      {open && <DecisionCase key={open} base={`/agent/cases/${open}`} variant="agent" seed={seedFor?.id === open ? seedFor.text : undefined} onCase={(d) => { setCur(d); load(); }} />}
      {open && cur?.result?.choices?.length > 0 && <HelmShareComparison key={`share-${open}-${cur.updated_at}`} caseId={open} />}
      {open && cur && !cur.household_id && <AttachToHousehold caseId={open} onAttached={() => { load(); setCur({ ...cur, household_id: "saved" }); }} />}
    </div>
  );
};
